// DataUsageProgress.js - Komponenta pro zobrazení využití dat
import React, { useState, useEffect } from 'react';
import { Database, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { dataUsage } from '../../services/api';
import { useTranslation } from 'react-i18next';

const DataUsageProgress = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    fetchDataUsage();
  }, []);

  const fetchDataUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await dataUsage.get();
      setUsageData(data);
    } catch (err) {
      console.error('Error fetching data usage:', err);
      setError('Nepodařilo se načíst informace o využití dat');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusIcon = (warningLevel) => {
    switch (warningLevel) {
      case 'danger':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      default:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };

  const getStatusText = (warningLevel, isOverLimit) => {
    if (isOverLimit) return 'Překročen limit!';
    switch (warningLevel) {
      case 'danger':
        return 'Blíží se limit';
      case 'warning':
        return 'Pozor na limit';
      default:
        return 'V pořádku';
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg border animate-pulse">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-6 h-6 bg-gray-200 rounded"></div>
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
        </div>
        <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg border border-red-200">
        <div className="flex items-center space-x-3 mb-2">
          <XCircle className="h-6 w-6 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900">Využití dat</h3>
        </div>
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={fetchDataUsage}
          className="mt-3 text-sm text-blue-600 hover:text-blue-700"
        >
          Zkusit znovu
        </button>
      </div>
    );
  }

  if (!usageData) return null;

  return (
    <div className="bg-white p-6 rounded-lg border">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Database className="h-6 w-6 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Využití dat</h3>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusIcon(usageData.status.warningLevel)}
          <span className={`text-sm font-medium ${
            usageData.status.isOverLimit 
              ? 'text-red-600' 
              : usageData.status.warningLevel === 'warning' 
                ? 'text-yellow-600' 
                : 'text-green-600'
          }`}>
            {getStatusText(usageData.status.warningLevel, usageData.status.isOverLimit)}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            {usageData.formatted.usageText}
          </span>
          <span className="text-sm text-gray-500">
            {usageData.usagePercentage}%
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getProgressColor(usageData.usagePercentage)}`}
            style={{ width: `${Math.min(100, usageData.usagePercentage)}%` }}
          ></div>
          {/* Indikátor překročení limitu */}
          {usageData.usagePercentage > 100 && (
            <div className="absolute top-0 right-0 h-full w-1 bg-red-700"></div>
          )}
        </div>
      </div>

      {/* Statistiky */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Celkový počet souborů:</span>
          <div className="font-medium">{usageData.totalFiles.toLocaleString()}</div>
        </div>
        <div>
          <span className="text-gray-500">Zbývající místo:</span>
          <div className="font-medium">{usageData.formatted.remainingSize}</div>
        </div>
      </div>

      {/* Top projekty */}
      {usageData.projectStats && usageData.projectStats.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Největší projekty podle velikosti:
          </h4>
          <div className="space-y-2">
            {usageData.projectStats.slice(0, 3).map((project, index) => (
              <div key={project.projectId} className="flex justify-between items-center text-xs">
                <span className="text-gray-600 truncate flex-1">
                  {index + 1}. {project.projectName}
                </span>
                <div className="flex items-center space-x-2 ml-2">
                  <span className="text-gray-500">
                    {project.formattedSize}
                  </span>
                  <span className="text-gray-400">
                    ({project.percentageOfTotal}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upozornění při dosažení limitu */}
      {usageData.status.isNearLimit && (
        <div className={`mt-4 p-3 rounded-lg ${
          usageData.status.isOverLimit 
            ? 'bg-red-50 border border-red-200' 
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-start space-x-2">
            {usageData.status.isOverLimit ? (
              <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
            )}
            <div className="text-sm">
              <div className={`font-medium ${
                usageData.status.isOverLimit ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {usageData.status.isOverLimit 
                  ? 'Překročili jste datový limit!'
                  : 'Blížíte se k datovému limitu!'
                }
              </div>
              <div className={`mt-1 ${
                usageData.status.isOverLimit ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {usageData.status.isOverLimit 
                  ? 'Doporučujeme smazat nepotřebné soubory nebo kontaktovat správce pro navýšení limitu.'
                  : 'Zvažte smazání starých nebo nepotřebných souborů.'
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataUsageProgress;